import { motion, AnimatePresence } from "framer-motion";
import { Rocket, ArrowRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useBrand } from "@/contexts/BrandContext";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { isRecargasPlan } from "@/lib/credits-plan";

interface WelcomeOverlayProps {
  visible: boolean;
  onStartTour: () => void;
  onSkip: () => void;
}

export function WelcomeOverlay({ visible, onStartTour, onSkip }: WelcomeOverlayProps) {
  const brand = useBrand();
  const { agent, activeWorkspaceId } = useAuth();
  const [planId, setPlanId] = useState<string | null>(null);

  useEffect(() => {
    if (!visible || !activeWorkspaceId) return;
    let cancelled = false;
    Promise.resolve(supabase.from("organizations").select("plan").eq("id", activeWorkspaceId).maybeSingle()).then(({ data }) => {
      if (!cancelled) setPlanId((data as { plan?: string | null } | null)?.plan ?? null);
    });
    return () => { cancelled = true; };
  }, [visible, activeWorkspaceId]);
  const firstName = agent?.full_name?.split(" ")[0] || "";

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed inset-0 z-[100] flex items-center justify-center"
          style={{ background: "linear-gradient(135deg, hsl(var(--primary) / 0.95) 0%, hsl(220 60% 15% / 0.97) 100%)" }}
        >
          {/* Skip button */}
          <button
            onClick={onSkip}
            className="absolute top-6 right-6 text-white/50 hover:text-white/90 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>

          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-col items-center text-center px-6 max-w-lg"
          >
            {/* Animated logo */}
            <motion.div
              initial={{ scale: 0, rotate: -20 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.4 }}
              className="mb-8"
            >
              <div className="h-20 w-20 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center border border-white/20 shadow-2xl">
                <Rocket className="h-10 w-10 text-white" />
              </div>
            </motion.div>

            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="text-3xl sm:text-4xl font-display font-bold text-white mb-3"
            >
              ¡Bienvenido a {brand.shortName}{firstName ? `, ${firstName}` : ""}! 🚀
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.5 }}
              className="text-lg text-white/80 mb-2 leading-relaxed"
            >
              Desde ahora, todos tus canales en un solo lugar.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.95, duration: 0.5 }}
              className="text-sm text-white/60 mb-8 leading-relaxed max-w-md"
            >
              WhatsApp, Instagram, Messenger, Email y Web Chat — unificados con IA, campañas y automatizaciones.{" "}
              {isRecargasPlan(planId) ? (
                <>Tu cuenta <span className="text-white font-semibold">paga con créditos</span> y no tiene cuota mensual: cada conversación se paga con tu saldo de créditos. Recarga tu saldo para empezar a atender.</>
              ) : (
                <>Tienes <span className="text-white font-semibold">todas las funciones gratis</span> hasta {brand.id === "etb" ? "100" : "50"} conversaciones/mes.</>
              )}
            </motion.p>

            {/* Features pill row */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1, duration: 0.5 }}
              className="flex flex-wrap justify-center gap-2 mb-10"
            >
              {["Asistentes IA 24/7", "CRM integrado", "Campañas masivas", "Reportes en vivo"].map((feature) => (
                <span
                  key={feature}
                  className="px-3 py-1 rounded-full text-xs font-medium bg-white/15 text-white/90 border border-white/10"
                >
                  {feature}
                </span>
              ))}
            </motion.div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.3, duration: 0.5 }}
              className="flex flex-col items-center gap-3"
            >
              {/* variant="secondary" avoids the default variant's .gradient-flow
                  background-image, which can't be overridden by bg-white and would
                  otherwise leave a low-contrast gradient CTA here. */}
              <Button
                onClick={onStartTour}
                variant="secondary"
                size="lg"
                className="bg-white text-primary hover:bg-white/90 font-semibold text-base px-8 py-6 rounded-xl shadow-xl"
              >
                Explorar {brand.shortName}
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
              <button
                onClick={onSkip}
                className="text-xs text-white/40 hover:text-white/70 transition-colors"
              >
                Saltar tour y empezar
              </button>
            </motion.div>
          </motion.div>

          {/* Background decorations */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.08 }}
              transition={{ delay: 0.5, duration: 1 }}
              className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-white"
            />
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.05 }}
              transition={{ delay: 0.7, duration: 1 }}
              className="absolute -bottom-32 -left-32 w-80 h-80 rounded-full bg-white"
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
